// client/src/pages/LegalLayout.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";

import { setGlobalLang } from "../i18n.js";
import LangSelect from "../components/front/LangSelect.jsx";
import {
  LEGAL_LANGS,
  LEGAL_PAGES,
  LEGAL_UI,
  applyProviderVars,
  getLegalText,
  normalizeLegalLang,
} from "./legalContent.js";

const ANIM_MS = 360;

function getLegalSlug(pathname) {
  const parts = String(pathname || "").split("/").filter(Boolean);
  if (LEGAL_LANGS.includes(parts[0])) return parts[1] || "pricing";
  return parts[0] || "pricing";
}

const HOME_ARIA_LABEL = {
  de: "Zur Startseite",
  en: "Go to homepage",
  uk: "На головну",
  fr: "Aller à la page d’accueil",
  nl: "Ga naar de startpagina",
  pl: "Przejdź do strony głównej",
  it: "Vai alla homepage",
  da: "Gå til forsiden",
  es: "Ir a la página principal",
  no: "Gå til startsiden",
};

export default function LegalLayout({ pageKey, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const rawLang = params.lang;
  const lang = normalizeLegalLang(rawLang);
  const slug = getLegalSlug(location.pathname);

  const [open, setOpen] = useState(false);
  const [legalMenuOpen, setLegalMenuOpen] = useState(false);

  const closingRef = useRef(false);
  const timerRef = useRef(null);

  const closeLegalMenu = useCallback(() => {
  setLegalMenuOpen(false);
}, []);

const toggleLegalMenu = useCallback(() => {
  setLegalMenuOpen((prev) => !prev);
}, []);

  useEffect(() => {
    if (!LEGAL_LANGS.includes(String(rawLang || "").toLowerCase())) {
      navigate(`/de/${slug}`, { replace: true });
    }
  }, [rawLang, slug, navigate]);

  useEffect(() => {
    setGlobalLang(lang);

    try {
      localStorage.setItem("lang", lang);
      localStorage.setItem("landingLang", lang);
    } catch {
      // ignore storage errors
    }
  }, [lang]);

  useEffect(() => {
    const t = setTimeout(() => setOpen(true), 20);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const closeTo = useCallback(
    (to) => {
      if (closingRef.current) return;

      closeLegalMenu();

      closingRef.current = true;
      setOpen(false);

      timerRef.current = setTimeout(() => {
        if (to === "BACK") navigate(`/${lang}/`, { replace: true });
        else navigate(to);

        closingRef.current = false;
      }, ANIM_MS);
    },
    [navigate, lang, closeLegalMenu]
  );

  useEffect(() => {
  const onKeyDown = (e) => {
    if (e.key !== "Escape") return;

    if (legalMenuOpen) {
      closeLegalMenu();
      return;
    }

    closeTo("BACK");
  };

  document.addEventListener("keydown", onKeyDown);
  return () => document.removeEventListener("keydown", onKeyDown);
}, [closeTo, legalMenuOpen, closeLegalMenu]);

  const t = useCallback(
    (value) => getLegalText(value, lang),
    [lang]
  );

  const handleLangChange = (nextLang) => {
  const next = normalizeLegalLang(nextLang);

  if (next === lang) {
    closeLegalMenu();
    return;
  }

  closeTo(`/${next}/${slug}`);
};
  

  const navItems = useMemo(
    () => [
      { slug: "pricing", label: LEGAL_UI.nav.pricing },
      { slug: "terms", label: LEGAL_UI.nav.terms },
      { slug: "privacy", label: LEGAL_UI.nav.privacy },
      { slug: "refund", label: LEGAL_UI.nav.refund },
      { slug: "impressum", label: LEGAL_UI.nav.impressum },
    ],
    []
  );

  const page = LEGAL_PAGES[pageKey] || LEGAL_PAGES.pricing;
  const title = t(page.title);
  const intro = applyProviderVars(t(page.intro), lang);

  const legalHeroImageClass = useMemo(() => {
  const map = {
    pricing: "legalPage__heroImage--pricing",
    terms: "legalPage__heroImage--terms",
    privacy: "legalPage__heroImage--privacy",
    refund: "legalPage__heroImage--refund",
    impressum: "legalPage__heroImage--impressum",
    contact: "legalPage__heroImage--contact",
  };

  return map[slug] || map[pageKey] || map.pricing;
}, [slug, pageKey]);

  const NavItem = ({ item }) => {
  const to = `/${lang}/${item.slug}`;
  const active = slug === item.slug;

  return (
    <a
      className={`legalPage__navLink ${active ? "legalPage__navLink--active" : ""}`}
      href={to}
      onClick={(e) => {
        e.preventDefault();

        if (active) {
          closeLegalMenu();
          return;
        }

        closeTo(to);
      }}
    >
      {t(item.label)}
    </a>
  );
};

  const goBack = useCallback(() => {
  if (closingRef.current) return;

  closingRef.current = true;
  setOpen(false);

  timerRef.current = setTimeout(() => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(`/${lang}/`, { replace: true });
    }

    closingRef.current = false;
  }, ANIM_MS);
}, [navigate, lang]);

  return (
    <div className={`legalPage ${open ? "legalPage--open" : ""}`}>
      <header className="legalPage__top">
        <div className="legalPage__topInner">
          <div className="header--seo__logo-lang legalPage__topInner--logo">
            <div
              className="legalPage__back"
              onClick={goBack}
            >
            </div>

            <Link
              to={`/${lang}/`}
              className="logo-wrapper-land logo-wrapper-land--legal"
              aria-label={HOME_ARIA_LABEL[lang] || HOME_ARIA_LABEL.en}
              onClick={closeLegalMenu}
            />

            <LangSelect
              value={lang}
              onChange={handleLangChange}
              availableLangs={LEGAL_LANGS}
            />
          </div>

          <div className="legalPage__topInner--right">
            <nav
              id="legalPageNav"
              className={`legalPage__nav${legalMenuOpen ? " legalPage__nav--open" : ""}`}
              aria-label="Legal navigation"
            >
              {navItems.map((item) => (
                <NavItem key={item.slug} item={item} />
              ))}
            </nav>

            <button
              type="button"
              className={`legalPage__menuButton${
                legalMenuOpen ? " legalPage__menuButton--open" : ""
              }`}
              aria-label="Open menu"
              aria-pressed={legalMenuOpen}
              aria-expanded={legalMenuOpen}
              aria-controls="legalPageNav"
              onClick={toggleLegalMenu}
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </div>
      </header>

      <main className="legalPage__main">
        <section className="legalPage__heroArea">
        <div className={`legalPage__heroImageFrame ${legalHeroImageClass}`} />

        <div className="legalPage__heroTitleBlock">
          <h1 className="legalPage__title">{title}</h1>

          {intro ? (
            <p className="text__landing legalPage__intro">
              {intro}
            </p>
          ) : null}
        </div>
      </section>

        <section className="legalPage__card">
          {typeof children === "function" ? children({ lang, t, page }) : children}
        </section>
      </main>

      <footer className="legalPage__footer">
        <div>© {new Date().getFullYear()} best-place.club</div>
      </footer>
    </div>
  );
}
